import Axios from 'axios';
import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { LoadingApp } from '../components/custom/CustomComponents';
import { Respuesta, temporadaActual } from '../utils/UtilesContexto';
import Reducer, { OBTENER_LISTAS } from './Reducer';
import {
  INICIAR_SESION,
  MODIFICAR_CATEGORIA,
  MODIFICAR_DIVISION,
  MODIFICAR_GRUPO,
  MODIFICAR_TEMPORADA,
  OBTENER_LISTAS_ACCESIBLES,
  OBTENER_LOCALIDADES,
  OBTENER_NOMBRES_LOCALIDADES,
  OBTENER_TODOS_JUGADORES,
} from './Reducer';

// Configurar timeout global de 20 segundos para evitar peticiones colgadas y saturacion de sockets
Axios.defaults.timeout = 20000;

/**
 * Control del contexto global de la aplicacion y acceso a la API.
 */
export const Context = createContext();

/**
 * Carga la configuración del entorno desde un archivo `config.env`.
 *
 * @returns {Promise<Object>} Un objeto que contiene las variables de entorno como pares clave-valor.
 * @throws {Error} Si ocurre un error al intentar leer o procesar el archivo `config.env`.
 */
export const loadEnvConfig = async () => {
  const response = await fetch('/config.env');
  const text = await response.text();
  const envVars = {};
  text.split('\n').forEach((line) => {
    if (line.trim() && !line.startsWith('#')) {
      const [key, value] = line.split('=');
      envVars[key.trim()] = value.trim();
    }
  });

  return envVars;
};

export const ContextProvider = (props) => {
  const defaultState = useMemo(
    () => ({
      idUsuario: undefined,
      temporadaActual: temporadaActual(),
      temporada: undefined,
      categoria: undefined,
      division: undefined,
      grupo: undefined,
      localidades: [],
      nombresLocalidades: [],
      todosJugadores: [],
      todosEquipos: [],
      listas: [],
      listasAccesibles: null,
      usuarioNombre: '',
      clubNombre: '',
      idClub: undefined,
    }),
    []
  );

  const [state, dispatch] = useReducer(Reducer, defaultState);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const [loading, setLoading] = useState(true);
  const [datosHomeCache, setDatosHomeCacheState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('datosHomeCache');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setDatosHomeCache = useCallback((data) => {
    setDatosHomeCacheState(data);
    try {
      if (data) {
        sessionStorage.setItem('datosHomeCache', JSON.stringify(data));
      } else {
        sessionStorage.removeItem('datosHomeCache');
      }
    } catch (e) {
      console.warn('Error al guardar datosHomeCache en sessionStorage:', e);
    }
  }, []);

  const [jugadoresManualesCache, setJugadoresManualesCacheState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('jugadoresManualesCache');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setJugadoresManualesCache = useCallback((data) => {
    setJugadoresManualesCacheState(data);
    try {
      if (data) {
        sessionStorage.setItem('jugadoresManualesCache', JSON.stringify(data));
      } else {
        sessionStorage.removeItem('jugadoresManualesCache');
      }
    } catch (e) {
      console.warn('Error al guardar jugadoresManualesCache en sessionStorage:', e);
    }
  }, []);

  const [informesUsuarioCache, setInformesUsuarioCacheState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('informesUsuarioCache');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setInformesUsuarioCache = useCallback((data) => {
    setInformesUsuarioCacheState(data);
    try {
      if (data) {
        sessionStorage.setItem('informesUsuarioCache', JSON.stringify(data));
      } else {
        sessionStorage.removeItem('informesUsuarioCache');
      }
    } catch (e) {
      console.warn('Error al guardar informesUsuarioCache en sessionStorage:', e);
    }
  }, []);

  const [equiposCache, setEquiposCacheState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('equiposCache');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const setEquipoCacheItem = useCallback((idEquipo, partialData) => {
    if (!idEquipo) {
      return;
    }
    const key = String(idEquipo);
    setEquiposCacheState((prev) => {
      const current = prev?.[key] || {};
      const currentTemporadas = current.temporadas || {};
      const incomingTemporadas = partialData.temporadas || {};

      const mergedTemporadas = { ...currentTemporadas };
      Object.keys(incomingTemporadas).forEach((idLiga) => {
        mergedTemporadas[idLiga] = {
          ...(currentTemporadas[idLiga] || {}),
          ...incomingTemporadas[idLiga],
        };
      });

      const updated = {
        ...prev,
        [key]: {
          ...current,
          ...partialData,
          temporadas: mergedTemporadas,
        },
      };
      try {
        sessionStorage.setItem('equiposCache', JSON.stringify(updated));
      } catch (e) {
        console.warn('Error al guardar equiposCache en sessionStorage:', e);
      }
      return updated;
    });
  }, []);

  const [ligasExplorarCache, setLigasExplorarCacheState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('ligasExplorarCache');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const setLigasExplorarCache = useCallback((temporada, ligas) => {
    if (!temporada) {
      return;
    }
    setLigasExplorarCacheState((prev) => {
      const updated = { ...prev, [temporada]: ligas };
      try {
        sessionStorage.setItem('ligasExplorarCache', JSON.stringify(updated));
      } catch (e) {
        console.warn('Error al guardar ligasExplorarCache en sessionStorage:', e);
      }
      return updated;
    });
  }, []);

  const [temporadasCache, setTemporadasCacheState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('temporadasCache');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setTemporadasCache = useCallback((temporadas) => {
    setTemporadasCacheState(temporadas);
    try {
      if (temporadas) {
        sessionStorage.setItem('temporadasCache', JSON.stringify(temporadas));
      } else {
        sessionStorage.removeItem('temporadasCache');
      }
    } catch (e) {
      console.warn('Error al guardar temporadasCache en sessionStorage:', e);
    }
  }, []);

  const [todosClubesCache, setTodosClubesCacheState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('todosClubesCache');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setTodosClubesCache = useCallback((clubes) => {
    setTodosClubesCacheState(clubes);
    try {
      if (clubes) {
        sessionStorage.setItem('todosClubesCache', JSON.stringify(clubes));
      } else {
        sessionStorage.removeItem('todosClubesCache');
      }
    } catch (e) {
      console.warn('Error al guardar todosClubesCache en sessionStorage:', e);
    }
  }, []);

  const [ligasCache, setLigasCacheState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('ligasCache');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const setLigaCacheItem = useCallback((idLiga, partialData) => {
    if (!idLiga) {
      return;
    }
    const key = String(idLiga);
    setLigasCacheState((prev) => {
      const current = prev?.[key] || {};
      const updated = {
        ...prev,
        [key]: {
          ...current,
          ...partialData,
        },
      };
      try {
        sessionStorage.setItem('ligasCache', JSON.stringify(updated));
      } catch (e) {
        console.warn('Error al guardar ligasCache en sessionStorage:', e);
      }
      return updated;
    });
  }, []);

  const [partidosCache, setPartidosCacheState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('partidosCache');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const setPartidoCacheItem = useCallback((idPartido, partialData) => {
    if (!idPartido) {
      return;
    }
    const key = String(idPartido);
    setPartidosCacheState((prev) => {
      const current = prev?.[key] || {};
      const updated = {
        ...prev,
        [key]: {
          ...current,
          ...partialData,
        },
      };
      try {
        sessionStorage.setItem('partidosCache', JSON.stringify(updated));
      } catch (e) {
        console.warn('Error al guardar partidosCache en sessionStorage:', e);
      }
      return updated;
    });
  }, []);

  const [jugadoresCache, setJugadoresCacheState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('jugadoresCache');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const setJugadorCacheItem = useCallback((idJugador, partialData) => {
    if (!idJugador) {
      return;
    }
    const key = String(idJugador);
    setJugadoresCacheState((prev) => {
      const current = prev?.[key] || {};
      const currentTemporadas = current.temporadas || {};
      const incomingTemporadas = partialData.temporadas || {};

      const mergedTemporadas = {
        ...currentTemporadas,
        ...incomingTemporadas,
      };

      const updated = {
        ...prev,
        [key]: {
          ...current,
          ...partialData,
          temporadas: mergedTemporadas,
        },
      };
      try {
        sessionStorage.setItem('jugadoresCache', JSON.stringify(updated));
      } catch (e) {
        console.warn('Error al guardar jugadoresCache en sessionStorage:', e);
      }
      return updated;
    });
  }, []);

  useEffect(() => {
    /**
     * Función asíncrona que carga la configuración del entorno y establece el estado inicial de la aplicación.
     */
    const fetchConfig = async () => {
      try {
        await loadEnvConfig();
      } catch (e) {
        console.warn('No se pudo cargar config.env, usando valores por defecto', e);
      }

      // Check for stored token and user info
      const storedToken = localStorage.getItem('token');
      const storedIdUsuario = localStorage.getItem('idUsuario');
      const storedIdClub = localStorage.getItem('idClub');
      const storedNombre = localStorage.getItem('nombre');
      const storedNombreClub = localStorage.getItem('nombreClub');

      if (storedToken && storedIdUsuario) {
        Axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      }

      const initialState = {
        ...defaultState,
        idUsuario: storedIdUsuario ? parseInt(storedIdUsuario, 10) : undefined,
        idClub: storedIdClub ? parseInt(storedIdClub, 10) : undefined,
        usuarioNombre: storedNombre || '',
        clubNombre: storedNombreClub || '',
        temporadaActual: temporadaActual(),
      };

      dispatch({ type: 'STATE_INICIAL', payload: initialState });
      setLoading(false);
    };

    // Configurar interceptor de Axios para manejar sesiones expiradas
    const interceptorId = Axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          // Si el token es inválido o ha expirado
          localStorage.removeItem('token');
          localStorage.removeItem('idUsuario');
          localStorage.removeItem('idClub');
          localStorage.removeItem('nombre');
          localStorage.removeItem('nombreClub');
          delete Axios.defaults.headers.common['Authorization'];
          // Redirigir al usuario al login (recarga la página y limpia el estado)
          window.location.href = '/iniciosesion';
        }
        return Promise.reject(error);
      }
    );

    fetchConfig();

    // Limpiar interceptor al desmontar
    return () => {
      Axios.interceptors.response.eject(interceptorId);
    };
  }, [defaultState]);

  /**
   * Inicio de sesion de un usuario en la aplicacion.
   */
  const iniciarSesion = async (usuario, contrasenaInput) => {
    const requestBody = { usuario: usuario, contrasena: contrasenaInput };
    // Login endpoint is public, no need for token header yet
    const axiosConfig = {
      method: 'POST',
      data: requestBody,
      url: `/api/usuarios/autenticar`,
    };
    try {
      const resultado = await Axios(axiosConfig);
      if (resultado.status !== 200) {
        return new Respuesta(false, 'El usuario no existe');
      } else if (resultado.data && resultado.data.token) {
        // Si ha iniciado sesion correctamente
        const { token, idUsuario, idClub, nombre, apellidos, nombreClub } = resultado.data;

        localStorage.setItem('token', token);
        localStorage.setItem('idUsuario', idUsuario);
        if (idClub) {
          const prevIdClub = localStorage.getItem('idClub');
          if (prevIdClub && String(prevIdClub) !== String(idClub)) {
            localStorage.removeItem('ultimoEquipoClub');
          }
          localStorage.setItem('idClub', idClub);
        }
        if (nombre) {
          localStorage.setItem('nombre', nombre + ' ' + apellidos);
        }
        if (nombreClub) {
          localStorage.setItem('nombreClub', nombreClub);
        }

        Axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        dispatch({
          type: INICIAR_SESION,
          payload: { idUsuario, idClub, nombre, nombreClub },
        });
        return new Respuesta(true, '');
      } else {
        return new Respuesta(false, 'Error de inicio de sesión. Verifica tus credenciales.');
      }
    } catch (error) {
      console.error(error);
      return new Respuesta(false, `Error inesperado al iniciar sesión. Inténtalo de nuevo`);
    }
  };

  /**
   * Cerrar sesión
   */
  const cerrarSesion = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('idUsuario');
    localStorage.removeItem('idClub');
    localStorage.removeItem('nombre');
    localStorage.removeItem('nombreClub');
    localStorage.removeItem('ultimoEquipoClub');
    delete Axios.defaults.headers.common['Authorization'];
    setDatosHomeCache(null);
    setJugadoresManualesCache(null);
    setInformesUsuarioCache(null);
    setEquiposCacheState({});
    setLigasExplorarCacheState({});
    setTemporadasCache(null);
    setTodosClubesCache(null);
    setLigasCacheState({});
    setPartidosCacheState({});
    setJugadoresCacheState({});
    try {
      sessionStorage.removeItem('datosHomeCache');
      sessionStorage.removeItem('jugadoresManualesCache');
      sessionStorage.removeItem('informesUsuarioCache');
      sessionStorage.removeItem('equiposCache');
      sessionStorage.removeItem('ligasExplorarCache');
      sessionStorage.removeItem('temporadasCache');
      sessionStorage.removeItem('todosClubesCache');
      sessionStorage.removeItem('ligasCache');
      sessionStorage.removeItem('partidosCache');
      sessionStorage.removeItem('jugadoresCache');
    } catch {
      // Ignore if sessionStorage not accessible
    }
    // You might want to dispatch an action to reset state or reload page
    window.location.href = '/';
  };

  /**
   * Recuperar todas las localidades
   */
  const obtenerLocalidades = async () => {
    try {
      const resultado = await Axios.get(`/api/localidades`);
      dispatch({ type: OBTENER_LOCALIDADES, payload: resultado.data });
      return resultado.data;
    } catch (error) {
      console.error('Error al obtener localidades:', error);
      return [];
    }
  };

  /**
   * Recuperar los nombres de todas las localidades
   */
  const obtenerNombresLocalidades = async () => {
    try {
      const resultado = await Axios.get(`/api/localidades/nombres`);
      dispatch({ type: OBTENER_NOMBRES_LOCALIDADES, payload: resultado.data });
      return resultado.data;
    } catch (error) {
      console.error('Error al obtener nombres de localidades:', error);
      return [];
    }
  };

  /**
   * Recuperar todos los jugadores
   */
  const obtenerTodosJugadores = async () => {
    try {
      const resultado = await Axios.get(`/api/jugadores/nombres`);
      dispatch({ type: OBTENER_TODOS_JUGADORES, payload: resultado.data });
      return resultado.data;
    } catch (error) {
      console.error('Error al obtener jugadores:', error);
      return [];
    }
  };

  /**
   * Recuperar todos los clubes (con soporte para cache SWR)
   * @param {boolean} forceRefresh - Forzar petición de red al backend
   */
  const obtenerTodosClubes = async (forceRefresh = false) => {
    if (!forceRefresh && todosClubesCache && todosClubesCache.length > 0) {
      return todosClubesCache;
    }
    try {
      const resultado = await Axios.get(`/api/clubes/nombres`);
      if (resultado.data) {
        setTodosClubesCache(resultado.data);
      }
      return resultado.data;
    } catch (error) {
      console.error('Error al obtener clubes:', error);
      return todosClubesCache || [];
    }
  };

  /**
   * Obtener los datos de un club por su ID
   */
  const obtenerDatosClub = async (idClub) => {
    try {
      return idClub ? (await Axios.get(`/api/clubes/${idClub}/datos`)).data : [];
    } catch (error) {
      console.error('Error al obtener datos de club:', error);
      return [];
    }
  };

  /**
   * Obtener todos los equipos de un club por su ID
   */
  const obtenerEquiposClub = async (idClub) => {
    try {
      return idClub ? (await Axios.get(`/api/clubes/${idClub}/equipos`)).data : [];
    } catch (error) {
      console.error('Error al obtener equipos de club:', error);
      return [];
    }
  };

  /**
   * Obtener todas las temporadas con datos (con soporte para cache SWR)
   * @param {boolean} forceRefresh - Forzar petición de red al backend
   */
  const obtenerTemporadas = useCallback(
    async (forceRefresh = false) => {
      if (!forceRefresh && temporadasCache && temporadasCache.length > 0) {
        return temporadasCache;
      }
      try {
        const data = (await Axios.get(`/api/temporadas/con_datos`)).data;
        if (data && data.length > 0) {
          setTemporadasCache(data);
        }
        return data;
      } catch (error) {
        console.error('Error al obtener temporadas:', error);
        return temporadasCache || [];
      }
    },
    [temporadasCache, setTemporadasCache]
  );

  /**
   * Obtener todas las ligas de una temporada (con soporte para cache SWR)
   * @param {string} temporada - Temporada de las ligas
   * @param {boolean} forceRefresh - Forzar petición de red al backend
   */
  const obtenerTodasLigas = useCallback(
    async (temporada, forceRefresh = false) => {
      if (!forceRefresh && temporada && ligasExplorarCache && ligasExplorarCache[temporada]) {
        return ligasExplorarCache[temporada];
      }
      try {
        const ligas = (await Axios.get(`/api/ligas/todas/${temporada}`)).data;
        if (ligas && temporada) {
          setLigasExplorarCache(temporada, ligas);
        }
        return ligas;
      } catch (error) {
        console.error('Error al obtener todas las ligas:', error);
        return (temporada && ligasExplorarCache && ligasExplorarCache[temporada]) || [];
      }
    },
    [ligasExplorarCache, setLigasExplorarCache]
  );

  /**
   * Obtener todas las categorias de una temporada (SENIOR, JUVENIL, etc)
   */
  const obtenerCategorias = useCallback(async (temporada) => {
    try {
      const valido = Boolean(temporada);
      return valido ? (await Axios.get(`/api/ligas/categorias/${temporada}`)).data : [];
    } catch (error) {
      console.error('Error al obtener categorias:', error);
      return [];
    }
  }, []);

  /**
   * Obtener todas las divisiones de una categoria en una temporada concreta (DH, NACIONAL, etc)
   */
  const obtenerDivisiones = useCallback(async (temporada, categoria) => {
    try {
      const valido = Boolean(temporada && categoria);
      return valido ? (await Axios.get(`/api/ligas/divisiones/${temporada}/${categoria}`)).data : [];
    } catch (error) {
      console.error('Error al obtener divisiones:', error);
      return [];
    }
  }, []);

  /**
   * Obtener todos los grupos de una división concreta (GRUPO NORTE, GRUPO SUR, etc)
   */
  const obtenerGrupos = useCallback(async (temporada, categoria, division) => {
    try {
      const valido = Boolean(temporada && categoria && division);
      return valido ? (await Axios.get(`/api/ligas/grupos/${temporada}/${categoria}/${division}`)).data : [];
    } catch (error) {
      console.error('Error al obtener grupos:', error);
      return [];
    }
  }, []);

  /**
   * Modificar la temporada actual del buscador
   */
  const modificarTemporada = useCallback(async (temporada) => {
    try {
      dispatch({ type: MODIFICAR_TEMPORADA, payload: temporada });
      return temporada;
    } catch (error) {
      console.error('Error al modificar temporada:', error);
      return null;
    }
  }, []);

  /**
   * Modificar la categoría actual del buscador
   */
  const modificarCategoria = useCallback(async (categoria) => {
    try {
      dispatch({ type: MODIFICAR_CATEGORIA, payload: categoria });
      return categoria;
    } catch (error) {
      console.error('Error al modificar categoria:', error);
      return null;
    }
  }, []);

  /**
   * Modificar la división actual del buscador
   */
  const modificarDivision = useCallback(async (division) => {
    try {
      dispatch({ type: MODIFICAR_DIVISION, payload: division });
      return division;
    } catch (error) {
      console.error('Error al modificar division:', error);
      return null;
    }
  }, []);

  /**
   * Modificar el grupo actual del buscador
   */
  const modificarGrupo = useCallback(async (grupo) => {
    try {
      dispatch({ type: MODIFICAR_GRUPO, payload: grupo });
      return grupo;
    } catch (error) {
      console.error('Error al modificar grupo:', error);
      return null;
    }
  }, []);

  /**
   * Obtener el ID de una liga con los parámetros del buscador (temporada,categoria,división,grupo)
   */
  const obtenerIdLiga = useCallback(async (temporada, categoria, division, grupo) => {
    try {
      const valido = Boolean(temporada && categoria && division && grupo);
      return valido ? (await Axios.get(`/api/ligas/id/${temporada}/${categoria}/${division}/${grupo}`)).data : null;
    } catch (error) {
      console.error('Error al obtener ID de liga:', error);
      return null;
    }
  }, []);

  /**
   * Obtener los datos de una liga por su ID
   */
  const obtenerDatosLiga = async (idLiga) => {
    try {
      return idLiga !== undefined ? (await Axios.get(`/api/ligas/${idLiga}/datos`)).data : [];
    } catch (error) {
      console.error('Error al obtener datos de liga:', error);
      return [];
    }
  };

  /**
   * Obtener la clasificación de una liga por su ID
   */
  const obtenerClasificacion = async (idLiga) => {
    try {
      return idLiga ? (await Axios.get(`/api/equipos-liga/${idLiga}/clasificacion`)).data : [];
    } catch (error) {
      console.error('Error al obtener clasificacion:', error);
      return [];
    }
  };

  /**
   * Obtener los partidos de una jornada concreta de una liga por su ID
   */
  const obtenerPartidosJornada = async (idLiga, jornada) => {
    try {
      return idLiga && jornada ? (await Axios.get(`/api/partidos/${idLiga}/jornada/${jornada}`)).data : [];
    } catch (error) {
      console.error('Error al obtener partidos de jornada:', error);
      return [];
    }
  };

  /**
   * Obtener la última jornada con datos de una liga por su ID
   */
  const obtenerUltimaJornada = async (idLiga) => {
    try {
      return idLiga ? (await Axios.get(`/api/ligas/${idLiga}/ultimajornada`)).data : 0;
    } catch (error) {
      console.error('Error al obtener ultima jornada:', error);
      return 0;
    }
  };

  /**
   * Obtener el número total de jornadas programadas de una liga por su ID
   */
  const obtenerTotalJornadas = async (idLiga) => {
    try {
      return idLiga ? (await Axios.get(`/api/ligas/${idLiga}/totaljornadas`)).data : 0;
    } catch (error) {
      console.warn('Endpoint totaljornadas no disponible:', error);
      return 0;
    }
  };

  /**
   * Obtener los jugadores guardados en listas de una liga por su ID
   */
  const obtenerJugadoresGuardados = async (idLiga) => {
    try {
      return idLiga ? (await Axios.get(`/api/jugadores-liga/${idLiga}/enlistas`)).data : [];
    } catch (error) {
      console.error('Error al obtener jugadores guardados:', error);
      return [];
    }
  };

  /**
   * Obtener los jugadores promocionados (de categoría inferior) de una liga por su ID
   */
  const obtenerJugadoresPromocionados = async (idLiga) => {
    try {
      return idLiga ? (await Axios.get(`/api/jugadores-liga/${idLiga}/promocionados`)).data : [];
    } catch (error) {
      console.error('Error al obtener jugadores promocionados:', error);
      return [];
    }
  };

  /**
   * Obtener los máximos goleadores de una liga por su ID
   */
  const obtenerMaximosGoleadores = async (idLiga) => {
    try {
      return idLiga ? (await Axios.get(`/api/jugadores-liga/${idLiga}/goleadores`)).data : [];
    } catch (error) {
      console.error('Error al obtener maximos goleadores:', error);
      return [];
    }
  };

  /**
   * Obtener los partidos con enlace de vídeo de una liga por su ID
   */
  const obtenerPartidosConEnlace = async (idLiga) => {
    try {
      return idLiga ? (await Axios.get(`/api/partidos/${idLiga}/partidos_enlace`)).data : [];
    } catch (error) {
      console.error('Error al obtener partidos con enlace:', error);
      return [];
    }
  };

  /**
   * Obtener los datos de un equipo por su ID
   */
  const obtenerDatosEquipo = async (idEquipo) => {
    try {
      return idEquipo ? (await Axios.get(`/api/equipos/${idEquipo}/datos`)).data : [];
    } catch (error) {
      console.error('Error al obtener datos de equipo:', error);
      return [];
    }
  };

  /**
   * Obtener los jugadores de un equipo por su ID
   */
  const obtenerPlantilla = async (idEquipo, idLiga) => {
    try {
      return idEquipo ? (await Axios.get(`/api/jugadores-liga/plantilla/${idEquipo}/${idLiga}`)).data : [];
    } catch (error) {
      console.error('Error al obtener plantilla:', error);
      return [];
    }
  };

  /**
   * Obtener la planificacion de un equipo por su ID y temporada
   */
  const obtenerPlanificacionEquipo = async (idEquipo, temporada) => {
    try {
      const res = idEquipo
        ? await Axios.get(`/api/planificacion/${idEquipo}?temporada=${encodeURIComponent(temporada)}`)
        : { data: [] };
      return res.status === 204 ? [] : res.data;
    } catch (error) {
      console.error('Error al obtener planificacion:', error);
      return [];
    }
  };

  /**
   * Guardar la planificacion de un equipo por su ID
   */
  const guardarPlanificacionEquipo = async (idEquipo, planificacion) => {
    try {
      if (!idEquipo) {
        return false;
      }
      console.log(planificacion);
      const resultado = await Axios.post(`/api/planificacion/${idEquipo}`, planificacion);
      return resultado.status === 201;
    } catch (error) {
      console.error('Error al guardar planificacion:', error);
      return false;
    }
  };

  /**
   * Obtener los últimos partidos jugados de una temporada concreta de un equipo por su ID
   */
  const obtenerUltimosPartidosEquipo = async (idEquipo, temporada) => {
    try {
      return idEquipo !== undefined
        ? (await Axios.get(`/api/partidos/equipo/${idEquipo}/temporada/${temporada}`)).data
        : [];
    } catch (error) {
      console.error('Error al obtener ultimos partidos de equipo:', error);
      return [];
    }
  };

  /**
   * Obtener el histórico de temporadas de un equipo por su ID
   */
  const obtenerHistoricoEquipo = async (idEquipo) => {
    try {
      return idEquipo ? (await Axios.get(`/api/equipos-liga/${idEquipo}/historico`)).data : [];
    } catch (error) {
      console.error('Error al obtener historico de equipo:', error);
      return [];
    }
  };

  const obtenerDatosJugador = async (idJugador) => {
    try {
      return idJugador ? (await Axios.get(`/api/jugadores/${idJugador}`)).data : null;
    } catch (error) {
      console.error('Error al obtener datos de jugador:', error);
      return null;
    }
  };

  /**
   * Crear un nuevo jugador
   */
  const crearJugador = async (datosJugador) => {
    try {
      const data = (await Axios.post('/api/jugadores', datosJugador)).data;
      obtenerTodosJugadores();
      setJugadoresManualesCache(null);
      return data;
    } catch (error) {
      console.error('Error al crear jugador:', error);
      return null;
    }
  };

  /**
   * Obtener jugadores creados manualmente (con soporte para cache SWR)
   * @param {number} idUsuario - ID del usuario
   * @param {boolean} forceRefresh - Forzar peticion de red al backend
   */
  const obtenerJugadoresManuales = async (idUsuario, forceRefresh = false) => {
    if (!forceRefresh && jugadoresManualesCache) {
      return jugadoresManualesCache;
    }
    try {
      const params = idUsuario ? { idUsuario } : {};
      const data = (await Axios.get('/api/jugadores/manuales', { params })).data;
      if (data) {
        setJugadoresManualesCache(data);
      }
      return data;
    } catch (error) {
      console.error('Error al obtener jugadores manuales:', error);
      return jugadoresManualesCache || [];
    }
  };

  /**
   * Obtener informes creados por un usuario (con soporte para cache SWR)
   * @param {number} idUsuario - ID del usuario
   * @param {boolean} forceRefresh - Forzar peticion de red al backend
   */
  const obtenerInformesUsuario = async (idUsuario, forceRefresh = false) => {
    if (!forceRefresh && informesUsuarioCache) {
      return informesUsuarioCache;
    }
    try {
      if (!idUsuario) {
        return [];
      }
      const data = (await Axios.get(`/api/informes/usuario/${idUsuario}`)).data;
      if (data) {
        setInformesUsuarioCache(data);
      }
      return data;
    } catch (error) {
      console.error('Error al obtener informes de usuario:', error);
      return informesUsuarioCache || [];
    }
  };

  /**
   * Obtener las observaciones de un jugador por su ID
   */
  const obtenerObservacionesJugador = async (idJugador) => {
    try {
      const config = state.idUsuario ? { params: { idUsuario: state.idUsuario } } : {};
      return idJugador ? (await Axios.get(`/api/jugadores/${idJugador}/observaciones`, config)).data : [];
    } catch (error) {
      console.error('Error al obtener observaciones de jugador:', error);
      return [];
    }
  };

  /**
   * Modificar los datos de un jugador por su ID
   */
  const modificarJugador = async (idJugador, detallesJugador) => {
    try {
      if (!idJugador) {
        return false;
      }
      const res = await Axios.patch(`/api/jugadores/editar/${idJugador}`, detallesJugador);
      if (res) {
        setJugadoresCacheState((prev) => {
          const next = { ...prev };
          delete next[String(idJugador)];
          try {
            sessionStorage.setItem('jugadoresCache', JSON.stringify(next));
          } catch (e) {
            console.warn('Error al actualizar jugadoresCache tras modificar:', e);
          }
          return next;
        });
      }
      return res;
    } catch (error) {
      console.error('Error al modificar jugador:', error);
      return false;
    }
  };

  /**
   * Eliminar un jugador por su ID
   */
  const eliminarJugador = async (idJugador) => {
    try {
      const res = idJugador ? await Axios.delete(`/api/jugadores/${idJugador}`) : false;
      if (res) {
        setJugadoresCacheState((prev) => {
          const next = { ...prev };
          delete next[String(idJugador)];
          try {
            sessionStorage.setItem('jugadoresCache', JSON.stringify(next));
          } catch (e) {
            console.warn('Error al guardar jugadoresCache en sessionStorage tras eliminar:', e);
          }
          return next;
        });
      }
      return res;
    } catch (error) {
      console.error('Error al eliminar jugador:', error);
      return false;
    }
  };

  /**
   * Modificar los datos de un jugador por su ID
   */
  const modificarEdadCheckJugador = async (idJugador, check) => {
    try {
      return idJugador ? await Axios.patch(`/api/jugadores/check-edad/${idJugador}/${check}`).data : false;
    } catch (error) {
      console.error('Error al modificar edad check de jugador:', error);
      return false;
    }
  };

  /**
   * Obtener el histórico de un jugador por su ID
   */
  const obtenerHistoricoJugador = async (idJugador) => {
    try {
      return idJugador ? (await Axios.get(`/api/jugadores-liga/historico/${idJugador}`)).data : false;
    } catch (error) {
      console.error('Error al obtener historico de jugador:', error);
      return false;
    }
  };

  /**
   * Obtener los últimos partidos de una temporada concreta de un jugador por su ID
   */
  /**
   * Obtener los últimos partidos de una temporada concreta de un jugador por su ID
   */
  const obtenerUltimosPartidosJugador = async (idJugador, temporada) => {
    try {
      return idJugador && temporada
        ? (await Axios.get(`/api/jugadores-partido/jugador/${idJugador}/temporada/${temporada}`)).data
        : [];
    } catch (error) {
      console.error('Error al obtener ultimos partidos de jugador:', error);
      return [];
    }
  };

  const anadirObservacion = async (idJugador, observacionDTO) => {
    try {
      const response = await Axios.post(
        `/api/jugadores/${idJugador}/observaciones?idUsuario=${state.idUsuario}`,
        observacionDTO
      );
      return response.data;
    } catch (error) {
      console.error('Error al añadir observacion:', error);
      return null;
    }
  };

  const editarObservacion = async (idObservacion, observacionDTO) => {
    try {
      const response = await Axios.put(
        `/api/jugadores/observaciones/${idObservacion}?idUsuario=${state.idUsuario}`,
        observacionDTO
      );
      return response.data;
    } catch (error) {
      console.error('Error al editar observacion:', error);
      return null;
    }
  };

  const eliminarObservacion = async (idObservacion) => {
    try {
      const response = await Axios.delete(`/api/jugadores/observaciones/${idObservacion}?idUsuario=${state.idUsuario}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar observacion:', error);
      return null;
    }
  };

  /**
   * Obtener los informes detallados de un jugador por su ID
   */
  const obtenerInformesJugador = async (idJugador) => {
    try {
      const config = state.idUsuario ? { params: { idUsuario: state.idUsuario } } : {};
      return idJugador ? (await Axios.get(`/api/informes/jugador/${idJugador}`, config)).data : [];
    } catch (error) {
      console.error('Error al obtener informes de jugador:', error);
      return [];
    }
  };

  const anadirInforme = async (idJugador, informeDTO) => {
    try {
      const response = await Axios.post(`/api/informes/jugador/${idJugador}?idUsuario=${state.idUsuario}`, informeDTO);
      setInformesUsuarioCache(null);
      return response.data;
    } catch (error) {
      console.error('Error al añadir informe:', error);
      return null;
    }
  };

  const editarInforme = async (idInforme, informeDTO) => {
    try {
      const response = await Axios.put(`/api/informes/${idInforme}?idUsuario=${state.idUsuario}`, informeDTO);
      setInformesUsuarioCache(null);
      return response.data;
    } catch (error) {
      console.error('Error al editar informe:', error);
      return null;
    }
  };

  const eliminarInforme = async (idInforme) => {
    try {
      const response = await Axios.delete(`/api/informes/${idInforme}?idUsuario=${state.idUsuario}`);
      setInformesUsuarioCache(null);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar informe:', error);
      return null;
    }
  };

  /**
   * Obtener los datos de un partido por su ID
   */
  const obtenerDatosPartido = async (idPartido) => {
    try {
      return (await Axios.get(`/api/partidos/${idPartido}`)).data;
    } catch (error) {
      console.error('Error al obtener datos de partido:', error);
      return [];
    }
  };

  /**
   * Obtener los jugadores de un equipo en un partido concreto por sus ID (equipo y partido)
   */
  const obtenerJugadoresPartido = async (idPartido, idEquipo) => {
    try {
      return (await Axios.get(`/api/jugadores-partido/jugadores/${idPartido}/${idEquipo}`)).data;
    } catch (error) {
      console.error('Error al obtener jugadores de partido:', error);
      return [];
    }
  };

  /**
   * Modificar el enlace de vídeo de un partido por su ID
   */
  const modificarEnlaceVideo = async (idPartido, enlaceVideo) => {
    try {
      return idPartido && enlaceVideo
        ? await Axios.patch(`/api/partidos/${idPartido}/enlace`, enlaceVideo, {
            headers: { 'Content-Type': 'text/plain' },
          })
        : null;
    } catch (error) {
      console.error('Error al modificar enlace de video:', error);
      return null;
    }
  };

  /**
   * Obtener los estados de evaluación de los partidos de un equipo
   */
  const obtenerEstadosEvaluacionEquipo = async (idEquipo, temporada) => {
    try {
      if (!idEquipo) {
        return [];
      }

      const queryParams = [];
      if (temporada) {
        queryParams.push(`temporada=${encodeURIComponent(temporada)}`);
      }
      if (state.idUsuario) {
        queryParams.push(`idUsuario=${state.idUsuario}`);
      }
      const params = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      return (await Axios.get(`/api/partidos/equipo/${idEquipo}/estados-evaluacion${params}`)).data;
    } catch (error) {
      console.error('Error al obtener estados de evaluacion del equipo:', error);
      return [];
    }
  };

  /**
   * Obtener la plantilla de un equipo con notas medias e histórico de rendimiento
   */
  const obtenerPlantillaRendimientoScouting = async (idEquipo, idLiga, temporada) => {
    try {
      if (!idEquipo) {
        return [];
      }
      const queryParams = [];
      if (idLiga) {
        queryParams.push(`idLiga=${idLiga}`);
      }
      if (temporada) {
        queryParams.push(`temporada=${encodeURIComponent(temporada)}`);
      }
      if (state.idUsuario) {
        queryParams.push(`idUsuario=${state.idUsuario}`);
      }
      const params = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      return (await Axios.get(`/api/jugadores-liga/plantilla/${idEquipo}/rendimiento-scouting${params}`)).data;
    } catch (error) {
      console.error('Error al obtener plantilla de rendimiento:', error);
      return [];
    }
  };

  /**
   * Obtener las evaluaciones de los jugadores de un partido
   */
  const obtenerEvaluacionesPartido = async (idPartido, idEquipo) => {
    try {
      if (!idPartido || !idEquipo) {
        return [];
      }
      const params = state.idUsuario ? `?idUsuario=${state.idUsuario}` : '';
      return (await Axios.get(`/api/evaluaciones/partido/${idPartido}/equipo/${idEquipo}${params}`)).data;
    } catch (error) {
      console.error('Error al obtener evaluaciones del partido:', error);
      return [];
    }
  };

  /**
   * Guardar o actualizar las evaluaciones de un partido
   */
  const guardarEvaluacionesPartido = async (idPartido, evaluaciones) => {
    try {
      if (!idPartido || !evaluaciones) {
        return false;
      }
      const params = state.idUsuario ? `?idUsuario=${state.idUsuario}` : '';
      const response = await Axios.post(`/api/evaluaciones/partido/${idPartido}/guardar${params}`, evaluaciones);
      return response.status === 200;
    } catch (error) {
      console.error('Error al guardar evaluaciones del partido:', error);
      return false;
    }
  };

  /**
   * Eliminar todas las evaluaciones de un partido para el usuario actual
   */
  const eliminarEvaluacionesPartido = async (idPartido) => {
    try {
      if (!idPartido) {
        return false;
      }
      const params = state.idUsuario ? `?idUsuario=${state.idUsuario}` : '';
      const response = await Axios.delete(`/api/evaluaciones/partido/${idPartido}${params}`);
      return response.status === 200;
    } catch (error) {
      console.error('Error al eliminar evaluaciones del partido:', error);
      return false;
    }
  };

  /**
   * Obtener el historial de partidos y valoraciones de un jugador en una temporada
   */
  const obtenerHistorialEvaluacionesJugador = useCallback(
    async (idJugador, temporada) => {
      try {
        if (!idJugador) {
          return [];
        }
        const queryParams = [];
        if (temporada) {
          queryParams.push(`temporada=${encodeURIComponent(temporada)}`);
        }
        if (state.idUsuario) {
          queryParams.push(`idUsuario=${state.idUsuario}`);
        }
        const qs = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
        return (await Axios.get(`/api/evaluaciones/jugador/${idJugador}/historial${qs}`)).data;
      } catch (error) {
        console.error('Error al obtener historial de evaluaciones del jugador:', error);
        return [];
      }
    },
    [state.idUsuario]
  );

  /**
   * Obtener todas las listas de jugadores existentes
   * @returns {Promise<Array>} Lista de listas de jugadores
   */
  const obtenerListas = async () => {
    try {
      const idUsuario = state.idUsuario;
      const url = idUsuario ? `/api/listas/usuario/${idUsuario}` : `/api/listas/todas`;
      const resultado = (await Axios.get(url)).data;
      dispatch({ type: OBTENER_LISTAS, payload: resultado });
      return resultado;
    } catch (error) {
      console.error('Error al obtener listas:', error);
      return [];
    }
  };

  /**
   * Obtiene todas las listas accesibles (propias y compartidas/públicas)
   */
  const obtenerListasAccesibles = useCallback(async (force = false) => {
    try {
      const idUsuario = stateRef.current.idUsuario;
      if (!idUsuario) {
        return [];
      }
      if (!force && stateRef.current.listasAccesibles !== null) {
        return stateRef.current.listasAccesibles;
      }
      try {
        const res = await Axios.get(`/api/listas/accesibles/${idUsuario}`);
        if (res && Array.isArray(res.data)) {
          dispatch({ type: OBTENER_LISTAS_ACCESIBLES, payload: res.data });
          return res.data;
        }
      } catch (endpointErr) {
        // Fallback a /home si el endpoint dedicado no está disponible
        console.warn('Fallback a /home para obtener listas accesibles:', endpointErr);
        const homeData = (await Axios.get(`/api/usuarios/${idUsuario}/home`)).data;
        if (homeData) {
          const misListas = homeData.misListas || [];
          const otrasCompartidas = (homeData.otrasListas || []).filter(
            (l) => l.visibilidad === 'COMPARTIDA' || l.visibilidad === 'PUBLICA'
          );
          const accesibles = [...misListas, ...otrasCompartidas];
          dispatch({ type: OBTENER_LISTAS_ACCESIBLES, payload: accesibles });
          return accesibles;
        }
      }
      return [];
    } catch (error) {
      console.error('Error al obtener listas accesibles:', error);
      return [];
    }
  }, []);

  /**
   * Obtener una lista de jugadores guardados por su ID, incluyendo su nombre y jugadores
   */
  const obtenerLista = useCallback(async (idLista) => {
    try {
      const idUsuario = stateRef.current.idUsuario;
      const config = idUsuario ? { params: { idUsuario } } : {};
      const lista = idLista !== undefined ? (await Axios.get(`/api/listas/id/${idLista}`, config)).data : false;
      return lista;
    } catch (error) {
      console.error('Error al obtener lista:', error);
      return false;
    }
  }, []);

  /**
   * Alternar el estado de favorita de una lista
   */
  const toggleFavorita = async (idLista) => {
    try {
      const idUsuario = state.idUsuario;
      if (!idUsuario || !idLista) {
        return false;
      }
      const config = { params: { idUsuario } };
      const res = await Axios.post(`/api/listas/favorita/${idLista}`, null, config);
      return res.data;
    } catch (error) {
      console.error('Error al modificar favorita:', error);
      return false;
    }
  };

  /**
   * Consultar las listas en las que está un jugador por su ID
   * @param {number} idJugador - ID del jugador
   * @returns {Promise<boolean>} true si el jugador está en alguna lista, false en caso contrario
   */
  const obtenerListasConJugador = async (idJugador) => {
    try {
      const idUsuario = state.idUsuario;
      const config = idUsuario ? { params: { idUsuario } } : {};
      return idJugador ? (await Axios.get(`/api/listas/jugador/${idJugador}`, config)).data : false;
    } catch (error) {
      console.error('Error al consultar si jugador está en lista:', error);
      return false;
    }
  };

  /**
   * Insertar un jugador en una lista por su ID (jugador y lista)
   */
  const insertarJugadorLista = async (idLista, idJugador) => {
    try {
      const idUsuario = state.idUsuario;
      const config = idUsuario ? { params: { idUsuario } } : {};
      return idLista && idJugador
        ? (await Axios.post(`/api/listas/insertar/${idLista}/${idJugador}`, null, config)).data
        : false;
    } catch (error) {
      console.error('Error al insertar jugador en lista:', error);
      return false;
    }
  };

  /**
   * Eliminar a un jugador de una lista por su ID (jugador y lista)
   */
  const eliminarJugadorLista = async (idLista, idJugador) => {
    try {
      return idLista && idJugador ? (await Axios.delete(`/api/listas/eliminar/${idLista}/${idJugador}`)).data : false;
    } catch (error) {
      console.error('Error al eliminar jugador de lista:', error);
      return false;
    }
  };

  /**
   * Crear una nueva lista
   * @param {string} nombre - Nombre de la lista
   * @param {number} idUsuario - ID del usuario propietario
   * @param {string} temporada - Temporada asociada a la lista
   * @returns {Promise<number|boolean>} Número de listas insertadas o false si error
   */
  const crearLista = async (nombre, idUsuario, temporada) => {
    try {
      const url = `/api/listas?nombre=${encodeURIComponent(nombre)}&idUsuario=${idUsuario}${temporada ? `&temporada=${encodeURIComponent(temporada)}` : ''}`;
      const resultado = await Axios.post(url);
      return resultado.data;
    } catch (error) {
      console.error('Error al crear lista:', error);
      return false;
    }
  };

  /**
   * Renombrar una lista
   * @param {number} idLista - ID de la lista
   * @param {string} nuevoNombre - Nuevo nombre de la lista
   * @returns {Promise<number|boolean>} Número de listas actualizadas o false si error
   */
  const renombrarLista = async (idLista, nuevoNombre) => {
    try {
      const resultado = await Axios.put(`/api/listas/${idLista}?nuevoNombre=${encodeURIComponent(nuevoNombre)}`);
      return resultado.data;
    } catch (error) {
      console.error('Error al renombrar lista:', error);
      return false;
    }
  };

  /**
   * Cambiar visibilidad de una lista
   * @param {number} idLista - ID de la lista
   * @param {string} visibilidad - 'PRIVADA', 'COMPARTIDA', 'PUBLICA'
   */
  const modificarVisibilidadLista = async (idLista, visibilidad) => {
    try {
      const resultado = await Axios.put(
        `/api/listas/${idLista}/visibilidad?visibilidad=${encodeURIComponent(visibilidad)}`
      );
      return resultado.data;
    } catch (error) {
      console.error('Error al cambiar visibilidad de lista:', error);
      return false;
    }
  };

  /**
   * Cambiar temporada de una lista
   * @param {number} idLista - ID de la lista
   * @param {string} temporada - La nueva temporada
   */
  const modificarTemporadaLista = async (idLista, temporada) => {
    try {
      const resultado = await Axios.put(`/api/listas/${idLista}/temporada?temporada=${encodeURIComponent(temporada)}`);
      return resultado.data;
    } catch (error) {
      console.error('Error al cambiar temporada de lista:', error);
      return false;
    }
  };

  /**
   * Eliminar una lista por su ID
   * @param {number} idLista - ID de la lista
   * @returns {Promise<number|boolean>} Número de listas eliminadas o false si error
   */
  const eliminarLista = async (idLista) => {
    try {
      const resultado = await Axios.delete(`/api/listas/${idLista}`);
      return resultado.data;
    } catch (error) {
      console.error('Error al eliminar lista:', error);
      return false;
    }
  };

  /**
   * Obtener los datos de la pantalla de inicio del usuario (con soporte para cache SWR)
   * @param {number} idUsuario - ID del usuario
   * @param {boolean} forceRefresh - Forzar peticion de red al backend
   */
  const obtenerDatosHome = async (idUsuario, forceRefresh = false) => {
    if (!forceRefresh && datosHomeCache) {
      return datosHomeCache;
    }
    try {
      const data = idUsuario ? (await Axios.get(`/api/usuarios/${idUsuario}/home`)).data : null;
      if (data) {
        setDatosHomeCache(data);
        const misListas = data.misListas || [];
        const otrasCompartidas = (data.otrasListas || []).filter(
          (l) => l.visibilidad === 'COMPARTIDA' || l.visibilidad === 'PUBLICA'
        );
        dispatch({
          type: OBTENER_LISTAS_ACCESIBLES,
          payload: [...misListas, ...otrasCompartidas],
        });
      }
      return data;
    } catch (error) {
      console.error('Error al obtener datos de home:', error);
      return datosHomeCache || null;
    }
  };

  /**
   * Obtener los datos de un usuario por su ID
   */
  const obtenerDatosUsuario = async (idUsuario) => {
    try {
      return idUsuario ? (await Axios.get(`/api/usuarios/${idUsuario}`)).data : null;
    } catch (error) {
      console.error('Error al obtener datos de usuario:', error);
      return null;
    }
  };

  /**
   * Actualizar los datos de un usuario
   */
  const actualizarUsuario = async (idUsuario, datos) => {
    try {
      const response = await Axios.put(`/api/usuarios/${idUsuario}`, datos);
      if (response.status === 200 && response.data) {
        if (datos.nombre) {
          localStorage.setItem('nombre', datos.nombre);
          dispatch({
            type: INICIAR_SESION,
            payload: {
              idUsuario: state.idUsuario,
              idClub: state.idClub,
              nombre: datos.nombre + ' ' + datos.apellidos,
              nombreClub: state.clubNombre,
            },
          });
        }
        return new Respuesta(true, 'Usuario actualizado correctamente');
      } else {
        return new Respuesta(false, 'Error al actualizar usuario');
      }
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      // Extract specific error message from backend response
      const errorMessage = error.response?.data?.error || 'Error al actualizar usuario';
      return new Respuesta(false, errorMessage);
    }
  };

  if (loading) {
    return <LoadingApp />;
  }

  // Exportamos el contexto
  return (
    <Context.Provider
      value={{
        // Atributos
        idUsuario: state.idUsuario,
        idClub: state.idClub,
        temporada: state.temporada,
        categoria: state.categoria,
        division: state.division,
        grupo: state.grupo,
        nombresLocalidades: state.nombresLocalidades,
        localidades: state.localidades,
        todosJugadores: state.todosJugadores,
        temporadaActual: state.temporadaActual,
        listas: state.listas,
        listasAccesibles: state.listasAccesibles,

        // Comun
        iniciarSesion,
        cerrarSesion,
        obtenerLocalidades,
        obtenerNombresLocalidades,
        obtenerTodosJugadores,
        obtenerTodosClubes,

        // Buscador
        obtenerTemporadas,
        obtenerTodasLigas,
        obtenerCategorias,
        obtenerDivisiones,
        obtenerGrupos,
        modificarTemporada,
        modificarCategoria,
        modificarDivision,
        modificarGrupo,

        // Liga
        obtenerIdLiga,
        obtenerDatosLiga,
        obtenerClasificacion,
        obtenerPartidosJornada,
        obtenerUltimaJornada,
        obtenerTotalJornadas,
        obtenerJugadoresGuardados,
        obtenerJugadoresPromocionados,
        obtenerMaximosGoleadores,
        obtenerPartidosConEnlace,

        modificarEnlaceVideo,

        // Equipo
        obtenerDatosEquipo,

        obtenerPlantilla,
        obtenerPlanificacionEquipo,
        guardarPlanificacionEquipo,
        obtenerUltimosPartidosEquipo,
        obtenerHistoricoEquipo,

        // Club
        obtenerDatosClub,
        obtenerEquiposClub,

        // Jugador
        modificarJugador,
        modificarEdadCheckJugador,
        obtenerDatosJugador,
        crearJugador,
        obtenerJugadoresManuales,
        eliminarJugador,
        obtenerHistoricoJugador,
        obtenerUltimosPartidosJugador,
        obtenerObservacionesJugador,
        anadirObservacion,
        editarObservacion,
        eliminarObservacion,
        obtenerInformesJugador,
        anadirInforme,
        editarInforme,
        eliminarInforme,
        obtenerInformesUsuario,
        informesUsuarioCache,
        setInformesUsuarioCache,

        // Partido
        obtenerDatosPartido,
        obtenerJugadoresPartido,
        obtenerEstadosEvaluacionEquipo,
        obtenerPlantillaRendimientoScouting,
        obtenerEvaluacionesPartido,
        guardarEvaluacionesPartido,
        eliminarEvaluacionesPartido,
        obtenerHistorialEvaluacionesJugador,

        // Lista

        lista: state.lista,
        obtenerListas,
        obtenerLista,
        obtenerListasAccesibles,
        toggleFavorita,
        obtenerListasConJugador,
        insertarJugadorLista,
        eliminarJugadorLista,
        crearLista,
        renombrarLista,
        eliminarLista,
        modificarVisibilidadLista,
        modificarTemporadaLista,

        // Home y Caches Globales
        obtenerDatosHome,
        obtenerDatosUsuario,
        actualizarUsuario,
        usuarioNombre: state.usuarioNombre,
        clubNombre: state.clubNombre,
        datosHomeCache,
        setDatosHomeCache,
        jugadoresManualesCache,
        setJugadoresManualesCache,
        equiposCache,
        setEquipoCacheItem,
        ligasExplorarCache,
        setLigasExplorarCache,
        temporadasCache,
        setTemporadasCache,
        todosClubesCache,
        setTodosClubesCache,
        ligasCache,
        setLigaCacheItem,
        partidosCache,
        setPartidoCacheItem,
        jugadoresCache,
        setJugadorCacheItem,
      }}
    >
      {props.children}
    </Context.Provider>
  );
};

ContextProvider.propTypes = {
  children: PropTypes.node,
};
